/**
 * Vet Free Gift Popup
 * - Opens immediately on `index.vet-home` ONLY when the section is rendered (Liquid tag gating).
 * - Adds selected gift variant to cart via `/cart/add.js`.
 * - Saves step on close; shows sticky banner when closed before completing; "Grab free product" reopens at saved step.
 */

const VET_GIFT_STORAGE_KEYS = {
  keepClosed: 'vet_free_gift_keep_closed',
  lastStep: 'vet_free_gift_last_step',
};

class VetFreeGiftPopup {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.customerId = root.dataset.customerId;

    this.stepsWrap = root.querySelector('[data-vet-gift-steps]');
    this.stepEls = this.stepsWrap ? Array.from(this.stepsWrap.querySelectorAll('[data-vet-gift-step]')) : [];
    this.stepLabelEl = root.querySelector('[data-vet-gift-step-label]');
    this.backBtn = root.querySelector('[data-vet-gift-back]');
    this.selectWrap = root.querySelector('.vet-free-gift-popup__select-wrap');

    this.details = root.querySelector('[data-vet-gift-details]');
    this.list = root.querySelector('[data-vet-gift-list]');
    this.selectLabelEl = root.querySelector('[data-vet-gift-select-label]');
    this.selectedWrap = root.querySelector('[data-vet-gift-selected]');
    this.errorEl = root.querySelector('[data-vet-gift-error]');

    this.closeEls = Array.from(root.querySelectorAll('[data-vet-gift-close]'));

    this.step5RedeemedEl = root.querySelector('[data-step5-variant="redeemed"]');
    this.confirmStepEl = root.querySelector('[data-vet-gift-confirm-step]');
    this.noRedeemStepEl = root.querySelector('[data-step6-variant="no-redeem"], [data-step5-variant="no-redeem"]');
    this.noRedeemListEl = root.querySelector('[data-step6-list], [data-step5-list], [data-vet-gift-list]');
    this.noRedeemListPopulated = false;

    this.successPopupEl = root.querySelector('[data-vet-gift-success-popup]');
    this.successHeadingEl = root.querySelector('[data-vet-gift-success-heading]');
    this.successMessageEl = root.querySelector('[data-vet-gift-success-message]');

    this.bannerEl = document.querySelector('[data-vet-free-gift-banner]');
    this.bannerWrapEl = document.querySelector('[data-vet-free-gift-banner-wrap]');

    this.selected = null;
    this.justRedeemed = false;

    this.hasOpenedThisLoad = false;
    this.hasClosedThisLoad = false;
  }

  getSectionsForCartAdd() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer
        .getSectionsToRender()
        .map((section) => section.id)
        .join(',');
    }
    return 'cart-icon-bubble';
  }

  renderCartIconBubbleFromSectionHtml(sectionHtml) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !sectionHtml) return;
    const cartDrawer = document.querySelector('cart-drawer');
    if (
      bubble.closest('.header-vet') &&
      cartDrawer &&
      typeof cartDrawer.renderCartIconBubbleForVetHeader === 'function'
    ) {
      cartDrawer.renderCartIconBubbleForVetHeader(bubble, sectionHtml);
      return;
    }
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const section = doc.querySelector('.shopify-section');
    if (section) bubble.innerHTML = section.innerHTML;
  }

  async addToCart(variantId, options = {}) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}cart/add.js`;
    const sections = this.getSectionsForCartAdd();

    const payload = {
      id: String(variantId),
      quantity: 1,
      properties: {
        _vet_free_gift: 'true',
      },
      sections,
      sections_url: window.location.pathname,
    };

    const res = await window.cartFetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.status) {
      const message = data?.description || data?.message || `Cart add failed (${res.status})`;
      throw new Error(message);
    }

    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'vet-free-gift-popup',
        productVariantId: String(variantId),
        cartData: data,
      });
    }

    const openCartDrawer = options.openCartDrawer !== false;
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
      cartDrawer.renderContents(data, { open: openCartDrawer });
    } else if (data.sections && data.sections['cart-icon-bubble']) {
      this.renderCartIconBubbleFromSectionHtml(data.sections['cart-icon-bubble']);
    }

    return data;
  }

  init() {
    this.bindEvents();
    this.bindStepHeader();
    this.buildGiftList();
    this.goToStep(1);

    const keepClosed = this.getStoredKeepClosed();
    const lastStep = this.getStoredLastStep();

    if (keepClosed) {
      this.hasOpenedThisLoad = true;
      return;
    }
    if (lastStep != null && lastStep >= 1 && lastStep <= this.stepEls.length) {
      this.hasOpenedThisLoad = true;
      this.showBanner();
      return;
    }
    this.open();
  }

  getStoredKeepClosed() {
    try {
      return sessionStorage.getItem(VET_GIFT_STORAGE_KEYS.keepClosed) === '1';
    } catch {
      return false;
    }
  }

  getStoredLastStep() {
    try {
      const v = sessionStorage.getItem(VET_GIFT_STORAGE_KEYS.lastStep);
      return v != null ? parseInt(v, 10) : null;
    } catch {
      return null;
    }
  }

  setStoredKeepClosed(value) {
    try {
      if (value) {
        sessionStorage.setItem(VET_GIFT_STORAGE_KEYS.keepClosed, '1');
        sessionStorage.removeItem(VET_GIFT_STORAGE_KEYS.lastStep);
      } else {
        sessionStorage.removeItem(VET_GIFT_STORAGE_KEYS.keepClosed);
      }
    } catch {}
  }

  setStoredLastStep(stepNumber) {
    try {
      if (stepNumber != null) {
        sessionStorage.setItem(VET_GIFT_STORAGE_KEYS.lastStep, String(stepNumber));
        sessionStorage.removeItem(VET_GIFT_STORAGE_KEYS.keepClosed);
      } else {
        sessionStorage.removeItem(VET_GIFT_STORAGE_KEYS.lastStep);
      }
    } catch {}
  }

  showBanner() {
    if (this.bannerEl) this.bannerEl.classList.add('is-visible');
    this.updateBannerFixedState();
  }

  hideBanner() {
    if (this.bannerEl) this.bannerEl.classList.remove('is-visible', 'is-at-top');
    if (this.bannerWrapEl) this.bannerWrapEl.style.height = '';
  }

  updateBannerFixedState() {
    if (!this.bannerEl || !this.bannerWrapEl) return;
    if (!this.bannerEl.classList.contains('is-visible')) {
      this.bannerEl.classList.remove('is-at-top');
      this.bannerWrapEl.style.height = '';
      return;
    }
    const wrapperTop = this.bannerWrapEl.getBoundingClientRect().top;
    if (wrapperTop <= 0) {
      this.bannerEl.classList.add('is-at-top');
      this.bannerWrapEl.style.height = `${this.bannerEl.offsetHeight}px`;
    } else {
      this.bannerEl.classList.remove('is-at-top');
      this.bannerWrapEl.style.height = '';
    }
  }

  openAtStep(stepNumber) {
    this.hasClosedThisLoad = false;
    if (this.stepsWrap) this.stepsWrap.style.display = '';
    if (this.successPopupEl) {
      this.successPopupEl.style.display = 'none';
      this.root.classList.remove('is-success-view');
    }
    const step = Math.min(this.stepEls.length, Math.max(1, parseInt(stepNumber, 10) || 1));
    this.goToStep(step);
    this.root.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.hideBanner();
  }

  bindEvents() {
    this.closeEls.forEach((el) => el.addEventListener('click', (e) => this.onClose(e)));

    const openEls = Array.from(document.querySelectorAll('[data-vet-free-gift-open]'));
    openEls.forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const lastStep = this.getStoredLastStep();
        if (
          lastStep != null &&
          lastStep >= 1 &&
          lastStep <= this.stepEls.length &&
          this.bannerEl &&
          this.bannerEl.contains(el)
        ) {
          this.openAtStep(lastStep);
        } else {
          this.onExplicitOpen(e);
        }
      });
    });

    document.querySelector('[data-vet-gift-banner-close]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.hideBanner();
    });

    window.addEventListener('scroll', () => this.updateBannerFixedState(), { passive: true });
    window.addEventListener('resize', () => this.updateBannerFixedState());

    this.details?.addEventListener('toggle', () => {
      if (this.details.open) {
        this.updateSelectLabel('open');
        this.goToStep(2);
      }
      if (!this.details.open && !this.selected) this.goToStep(1);
    });

    const nextEls = Array.from(this.root.querySelectorAll('[data-vet-gift-next]'));
    nextEls.forEach((el) =>
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const current = this.getCurrentStepNumber();
        if (current === this.getNoRedeemStepNumber()) {
          if (this.noRedeemStepEl?.classList.contains('has-selection') && this.selected?.variantId) {
            this.confirmNoRedeemSelection();
          }
          return;
        }
        if (current < this.stepEls.length) this.goToStep(current + 1);
      })
    );
  }

  open() {
    if (this.hasOpenedThisLoad || this.hasClosedThisLoad) return;
    this.hasOpenedThisLoad = true;
    this.root.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.hasClosedThisLoad = true;
    this.root.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
  }

  async confirmNoRedeemSelection() {
    if (!this.selected?.variantId) return;
    const variantId = parseInt(this.selected.variantId, 10);
    const productTitle = this.selected.productTitle || 'Product';
    const productDiscountCode = (this.selected.productDiscountCode || '').trim();
    this.setError('');
    try {
      await this.addToCart(variantId, { openCartDrawer: false });
    } catch (err) {
      this.setError(err?.message || 'Something went wrong. Please try again.');
      return;
    }
    const confirmStep = this.getConfirmStepNumber();
    if (confirmStep > 0) {
      this.justRedeemed = true;
      this.showConfirmImageFor(variantId);
      this.goToStep(confirmStep);
    } else {
      this.showSuccessView(productTitle);
    }
    if (productDiscountCode) {
      try {
        const shopRoot = window.Shopify?.routes?.root || '/';
        const discountPath = `${shopRoot}discount/${encodeURIComponent(productDiscountCode)}`;
        await fetch(discountPath, {
          method: 'GET',
          credentials: 'same-origin',
          redirect: 'manual',
        });
        if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vet-free-gift-popup' });
        }
      } catch (e) {
        console.warn('[vet-free-gift] discount apply failed:', e);
      }
    }
  }

  showSuccessView(productTitle) {
    if (!this.successPopupEl || !this.stepsWrap) return;
    const heading = this.root.dataset.successHeading || 'Product added!';
    if (this.successHeadingEl) this.successHeadingEl.textContent = heading;
    if (this.successMessageEl) {
      this.successMessageEl.innerHTML = '';
      const bold = document.createElement('span');
      bold.className = 'text--bold';
      bold.textContent = productTitle || '';
      this.successMessageEl.appendChild(bold);
      this.successMessageEl.appendChild(document.createTextNode(' has been added to your cart.'));
    }
    this.stepsWrap.style.display = 'none';
    this.successPopupEl.style.display = '';
    this.root.classList.add('is-success-view');
  }

  onClose(e) {
    e.preventDefault();

    const onSuccessView = this.root.classList.contains('is-success-view');
    const currentStep = this.getCurrentStepNumber();
    const hasStep6 = !!this.root.querySelector('[data-vet-gift-step="6"]');
    const onStep5Redeemed = currentStep === 5 && this.step5RedeemedEl?.classList.contains('is-active') && !hasStep6;

    const confirmStep = this.getConfirmStepNumber();
    const onConfirmStep = confirmStep > 0 && currentStep === confirmStep;

    if (onSuccessView || onStep5Redeemed || onConfirmStep) {
      this.setStoredKeepClosed(true);
      this.hideBanner();
    } else {
      this.setStoredLastStep(currentStep);
      this.setStoredKeepClosed(false);
      this.showBanner();
    }

    this.close();
  }

  onExplicitOpen(e) {
    e.preventDefault();
    this.root.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  goToStep(stepNumber) {
    if (!this.stepsWrap) return;
    const hasRedeemed = this.root.dataset.hasRedeemed === 'true';
    const showRedeemedVariant =
      stepNumber === 5 && (this.justRedeemed || hasRedeemed || !!this.root.querySelector('[data-vet-gift-step="6"]'));
    this.root.classList.toggle('is-success', showRedeemedVariant);
    this.root.classList.toggle('is-step-1', stepNumber === 1);
    this.stepEls.forEach((el) => {
      const step = parseInt(el.dataset.vetGiftStep, 10);
      el.classList.toggle('is-active', step === stepNumber);
    });
    if (this.stepLabelEl) {
      this.stepLabelEl.textContent = `STEP ${stepNumber} of ${this.stepEls.length}`;
    }
    if (this.backBtn) {
      // No going back from the confirmation step — the gift is already in the cart.
      const onConfirmStep = stepNumber === this.getConfirmStepNumber();
      this.backBtn.style.display = stepNumber >= 2 && !onConfirmStep ? '' : 'none';
    }
    const showSelectWrap = stepNumber === this.getNoRedeemStepNumber() && !this.justRedeemed && !hasRedeemed;
    if (this.selectWrap) {
      this.selectWrap.style.display = showSelectWrap ? '' : 'none';
    }
    if (stepNumber === this.getNoRedeemStepNumber()) {
      this.updateNoRedeemVariant();
    }
  }

  showConfirmImageFor(variantId) {
    const wrap = this.root.querySelector('[data-vet-confirm-image-wrap]');
    if (!wrap) return;

    const options = Array.from(wrap.children);
    options.forEach((el) => {
      el.hidden = true;
    });

    const wanted = String(variantId || '');
    const target =
      options.find((el) => el.dataset.vetConfirmImageFor === wanted) ||
      options.find((el) => el.dataset.vetConfirmImageFallback !== undefined);
    if (target) target.hidden = false;
  }

  getConfirmStepNumber() {
    if (!this.confirmStepEl) return -1;
    return parseInt(this.confirmStepEl.dataset.vetGiftStep, 10) || -1;
  }

  getNoRedeemStepNumber() {
    if (!this.noRedeemStepEl) return -1;
    const stepEl = this.noRedeemStepEl.closest('[data-vet-gift-step]');
    if (!stepEl) return -1;
    return parseInt(stepEl.dataset.vetGiftStep, 10) || -1;
  }

  updateNoRedeemVariant() {
    if (this.noRedeemStepEl) {
      this.noRedeemStepEl.classList.toggle('has-selection', !!this.selected);
      if (this.noRedeemListEl && this.list) {
        this.populateNoRedeemList();
      }
      if (this.selectedWrap) {
        this.selectedWrap.style.display = 'none';
      }
    }
  }

  isOnNoRedeemStep() {
    const noRedeemStep = this.getNoRedeemStepNumber();
    return noRedeemStep > 0 && this.getCurrentStepNumber() === noRedeemStep && !!this.noRedeemStepEl;
  }

  selectGiftNoRedeemOnly(gift) {
    this.selected = gift;
    this.setError('');
    if (this.selectedWrap) this.selectedWrap.style.display = 'none';
    const cards = Array.from(this.list.querySelectorAll('.vet-free-gift-popup__product'));
    cards.forEach((card) => card.classList.toggle('is-selected', card.dataset.productId === gift.productId));
    this.clearVariantSelectionOnOtherProducts(gift.productId);
    if (this.noRedeemStepEl) this.noRedeemStepEl.classList.add('has-selection');
  }

  clearVariantSelectionOnOtherProducts(selectedProductId) {
    if (!this.list) return;
    this.list.querySelectorAll('.vet-free-gift-popup__product').forEach((card) => {
      if (card.dataset.productId !== selectedProductId) {
        card.querySelectorAll('.vet-free-gift-popup__variant').forEach((v) => v.classList.remove('is-selected'));
      }
    });
  }

  populateNoRedeemList() {
    if (!this.noRedeemListEl || !this.list) return;
    if (this.noRedeemListEl === this.list) {
      this.noRedeemListPopulated = true;
      return;
    }
    if (this.noRedeemListPopulated) return;
    this.noRedeemListPopulated = true;

    this.noRedeemListEl.innerHTML = this.list.innerHTML;

    this.noRedeemListEl.querySelectorAll('.vet-free-gift-popup__product').forEach((card) => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = card.dataset.productId;
        if (!productId) return;

        const titleEl = card.querySelector('.vet-free-gift-popup__product-title');
        const imgEl = card.querySelector('.vet-free-gift-popup__product-image img');
        const selectedVariant = card.querySelector('.vet-free-gift-popup__variant.is-selected');
        const variants = card.querySelectorAll('.vet-free-gift-popup__variant');

        let variantId, label;
        if (variants.length > 1 && selectedVariant) {
          variantId = selectedVariant.dataset.variantId;
          label = selectedVariant.textContent || '';
        } else if (variants.length === 1) {
          variantId = variants[0].dataset.variantId;
          label = variants[0].textContent || '';
        } else {
          return;
        }
        if (!variantId) return;

        this.selectGiftNoRedeemOnly({
          variantId: variantId,
          productId: productId,
          productTitle: titleEl ? titleEl.textContent : '',
          label: label,
          imageUrl: imgEl ? imgEl.src : '',
          hasMultipleOptions: variants.length > 1,
          productDiscountCode: (card.dataset.productDiscountCode || '').trim(),
        });
      });
    });

    this.noRedeemListEl.querySelectorAll('.vet-free-gift-popup__variant').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const card = btn.closest('.vet-free-gift-popup__product');
        if (!card) return;
        card.querySelectorAll('.vet-free-gift-popup__variant').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
      });
    });
  }

  getCurrentStepNumber() {
    const active = this.stepEls.find((el) => el.classList.contains('is-active'));
    if (!active) return 1;
    return parseInt(active.dataset.vetGiftStep, 10) || 1;
  }

  bindStepHeader() {
    if (!this.backBtn) return;
    this.backBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const current = this.getCurrentStepNumber();
      if (current <= 1) return;
      this.goToStep(current - 1);
    });
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.style.display = message ? 'block' : 'none';
  }

  buildGiftList() {
    if (!this.list) return;

    const rawVariantEls = Array.from(this.root.querySelectorAll('[data-vet-gift-variant]'));
    if (!rawVariantEls.length) return;

    const grouped = new Map();
    rawVariantEls.forEach((el) => {
      const productId = el.dataset.productId;
      const variantId = el.dataset.variantId;
      const productTitle = el.dataset.productTitle;
      const label = el.dataset.label || productTitle;
      const imageUrl = el.dataset.imageUrl;
      const productDiscountCode = (el.dataset.productDiscountCode || '').trim();
      const subtitle = (el.dataset.subtitle || '').trim();

      if (!productId || !variantId) return;

      if (!grouped.has(productId)) {
        grouped.set(productId, { productId, productTitle, subtitle, imageUrl, productDiscountCode, variants: [] });
      }

      grouped.get(productId).variants.push({ variantId, label, discountCode: productDiscountCode });
    });

    this.list.innerHTML = '';
    Array.from(grouped.values()).forEach((group) => {
      const item = document.createElement('div');
      item.className = 'vet-free-gift-popup__product';
      item.dataset.productId = group.productId;
      if (group.productDiscountCode) item.dataset.productDiscountCode = group.productDiscountCode;

      const imgWrap = document.createElement('div');
      imgWrap.className = 'vet-free-gift-popup__product-image';
      if (group.imageUrl) {
        const img = document.createElement('img');
        img.src = group.imageUrl;
        img.alt = group.productTitle || '';
        img.loading = 'lazy';
        imgWrap.appendChild(img);
      }

      const content = document.createElement('div');
      content.style.flex = '1';

      const title = document.createElement('p');
      title.className = 'vet-free-gift-popup__product-title';
      title.textContent = group.productTitle || '';
      content.appendChild(title);

      if (group.subtitle) {
        const subtitle = document.createElement('p');
        subtitle.className = 'vet-free-gift-popup__product-subtitle text--xs text--medium';
        subtitle.textContent = group.subtitle;
        content.appendChild(subtitle);
      }

      if (group.variants.length > 1) {
        const variantsWrap = document.createElement('div');
        variantsWrap.className = 'vet-free-gift-popup__variants';

        group.variants.forEach((v) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'vet-free-gift-popup__variant';
          btn.textContent = v.label;
          btn.dataset.variantId = v.variantId;

          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            variantsWrap
              .querySelectorAll('.vet-free-gift-popup__variant')
              .forEach((b) => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');
            const gift = {
              variantId: v.variantId,
              productId: group.productId,
              productTitle: group.productTitle,
              subtitle: group.subtitle || '',
              label: v.label,
              imageUrl: group.imageUrl,
              hasMultipleOptions: true,
              productDiscountCode: v.discountCode || group.productDiscountCode || '',
            };
            if (this.isOnNoRedeemStep()) {
              this.selectGiftNoRedeemOnly(gift);
            } else {
              this.selectGift(gift);
            }
          });

          variantsWrap.appendChild(btn);
        });

        content.appendChild(variantsWrap);

        item.addEventListener('click', (e) => {
          e.preventDefault();
          const active = variantsWrap.querySelector('.vet-free-gift-popup__variant.is-selected');
          if (!active) return;
          const variantId = active.dataset.variantId;
          const label = active.textContent || group.productTitle;
          if (!variantId) return;

          const activeVariant = group.variants.find((v) => String(v.variantId) === String(variantId));

          const gift = {
            variantId,
            productId: group.productId,
            productTitle: group.productTitle,
            subtitle: group.subtitle || '',
            label,
            imageUrl: group.imageUrl,
            hasMultipleOptions: true,
            productDiscountCode: activeVariant?.discountCode || group.productDiscountCode || '',
          };
          if (this.isOnNoRedeemStep()) {
            this.selectGiftNoRedeemOnly(gift);
          } else {
            this.selectGift(gift);
          }
        });
      } else {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          const gift = {
            variantId: group.variants[0].variantId,
            productId: group.productId,
            productTitle: group.productTitle,
            subtitle: group.subtitle || '',
            label: group.variants[0].label,
            imageUrl: group.imageUrl,
            hasMultipleOptions: false,
            productDiscountCode: group.variants[0].discountCode || group.productDiscountCode || '',
          };
          if (this.isOnNoRedeemStep()) {
            this.selectGiftNoRedeemOnly(gift);
          } else {
            this.selectGift(gift);
          }
        });
      }

      item.appendChild(imgWrap);
      item.appendChild(content);

      this.list.appendChild(item);
    });
  }

  updateSelectLabel(state) {
    if (!this.selectLabelEl) return;
    const placeholder = this.selectLabelEl.dataset.placeholder || this.selectLabelEl.textContent;
    const openLabel = this.selectLabelEl.dataset.openLabel || placeholder;

    if (state === 'open' || state === 'selected') {
      this.selectLabelEl.textContent = openLabel;
    } else {
      this.selectLabelEl.textContent = placeholder;
    }
  }

  selectGift(gift) {
    this.selected = gift;
    this.setError('');
    this.goToStep(3);
    if (this.details) this.details.open = false;
    this.updateSelectLabel('selected');

    const cards = Array.from(this.list.querySelectorAll('.vet-free-gift-popup__product'));
    cards.forEach((card) => card.classList.toggle('is-selected', card.dataset.productId === gift.productId));
    this.clearVariantSelectionOnOtherProducts(gift.productId);

    if (this.selectedWrap) {
      this.selectedWrap.style.display = 'block';
      const showOption = Boolean(gift?.hasMultipleOptions && gift?.label);
      this.selectedWrap.innerHTML = `
        <div class="vet-free-gift-popup__product is-selected">
          <div class="vet-free-gift-popup__product-image">
            ${gift.imageUrl ? `<img src="${gift.imageUrl}" alt="${this.escapeHtml(gift.productTitle || '')}" loading="lazy">` : ''}
          </div>
          <div style="flex:1;">
            <p class="vet-free-gift-popup__product-title">${this.escapeHtml(gift.productTitle || '')}</p>
            ${
              gift.subtitle
                ? `<p class="vet-free-gift-popup__product-subtitle text--xs text--medium">${this.escapeHtml(gift.subtitle)}</p>`
                : ''
            }
            ${
              showOption
                ? `<div class="vet-free-gift-popup__variants">
                     <div class="vet-free-gift-popup__variant is-selected">${this.escapeHtml(gift.label || '')}</div>
                   </div>`
                : ''
            }
          </div>
        </div>
      `;
    }
  }

  escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const el = document.querySelector('[data-vet-free-gift-popup]');
  if (!el) return;
  if (el.dataset.template && el.dataset.template !== 'index.vet-home') return;

  const popup = new VetFreeGiftPopup(el);
  popup.init();
});

